import { DeepPartial } from '@core/util';
import { SourceCode } from './source-code.type';
import { ClassificationCode } from './classification-code.type';

export type IMyInfo = DeepPartial<MyInfo>;
export type IMyInfoAddress = DeepPartial<AddressBase>;

type MyInfo = Person & {
  uinfin: ValueBase<string>;
  nationality: CodeDescBase;
  birthcountry: CodeDescBase;
  residentialstatus: CodeDescBase;
  passportnumber: ValueBase<string>;
  passportexpirydate: ValueBase<string>;
  mobileno: MobileNumberBase;
  regadd: AddressBase;
  email: ValueBase<string>;
  cpfcontributions: CPFContributionHistoryBase;
  noahistory: NoticeOfAssessmentHistoryFullBase;
  ownerprivate: ValueBase<boolean | string>;
  marital: CodeDescBase;
  hdbownership: HdbOwnershipBase[];
  partialuinfin: ValueBase<string>;
  mailadd: AddressBase;
  billadd: AddressBase;
  housingtype: CodeDescBase;
  hdbtype: CodeDescBase;
  homeno: MobileNumberBase;
  marriagecertno: ValueBase<string>;
  countryofmarriage: CodeDescBase;
  marriagedate: ValueBase<string>;
  divorcedate: ValueBase<string>;
  childrenbirthrecords: ChildrenBirthRecordBase[];
  sponsoredchildrenrecords: SponsoredChildrenRecordBase[];
  schoolname: ValueCodeDescBase<string>;
  edulevel: CodeDescBase;
  passtype: CodeDescBase;
  gradyear: ValueBase<string>;
  occupation: ValueBase<string>;
  employment: ValueBase<string>;
  passstatus: ValueBase<string>;
  passexpirydate: ValueBase<string>;
  employmentsector: ValueBase<string>;
  householdincome: HouseHoldIncomeBase;
  vehicles: Vehicle[];
  drivinglicence: DrivingLicenceBase;
  academicqualifications: AcademicQualificationBase;
  merdekagen: MerdekaGenerationBase;
  silversupport: SilverSupportBase;
  gstvoucher: GSTVoucherBase;
  'noa-basic': NoticeOfAssessmentBasicBase;
  noa: NoticeOfAssessmentFullBase;
  'noahistory-basic': NoticeOfAssessmentHistoryBasicBase;
  cpfemployers: CPFEmployerHistoryBase;
  cpfbalances: CPFBalanceBase;
  cpfhousingwithdrawal: CPFHousingWithdrawalBase;
  // info: MyInfo;
};

export type ValueBase<T> = Value<T> & Partial<Base>;
export type CodeDescBase = CodeDesc & Partial<Base>;
export type ValueCodeDescBase<T> = Value<T> & CodeDescBase;
export type AddressBase = Address & Base;
export type CPFBalanceBase = Base & CPFBalance;
export type NoticeOfAssessmentBasicBase = Base & NoticeOfAssessmentBasic;
export type NoticeOfAssessmentHistory = NoticeOfAssessmentBasic & NoticeOfAssessment;
export type NoticeOfAssessmentFullBase = Base & NoticeOfAssessmentHistory;

export type Value<T> = {
  value: T;
};

export type Base = {
  classification: ClassificationCode;
  source: SourceCode;
  lastupdated: string;
};

export type CodeDesc<T = string> = {
  code: T;
  desc: string;
};

export type Address = {
  type: 'SG' | 'UNFORMATTED';
  block: Value<string>;
  building: Value<string>;
  floor: Value<string>;
  unit: Value<string>;
  street: Value<string>;
  line1: Value<string>;
  line2: Value<string>;
  postal: Value<string>;
  country: CodeDesc;
};

export type Person = {
  name: ValueBase<string>;
  aliasname: ValueBase<string>;
  hanyupinyinname: ValueBase<string>;
  hanyupinyinaliasname: ValueBase<string>;
  marriedname: ValueBase<string>;
  sex: CodeDescBase;
  race: CodeDescBase;
  dob: ValueBase<string>;
  secondaryrace: CodeDescBase;
  dialect: CodeDescBase;
};

export type HdbOwnershipBase = Base & {
  noofowners?: Value<number>;
  address: Address;
  hdbtype?: CodeDesc;
  leasecommencementdate?: Value<string>;
  termoflease?: Value<number>;
  dateofpurchase?: Value<string>;
  dateofownershiptransfer?: Value<string>;
  loangranted?: Value<number>;
  originalloanrepayment?: Value<number>;
  balanceloanrepayment?: {
    years: Value<number>;
    months: Value<number>;
  };
  outstandingloanbalance?: Value<number>;
  monthlyloaninstalment?: Value<number>;
  outstandinginstalment?: Value<number>;
  purchaseprice?: Value<number>;
};

export type MobileNumberBase = Base & {
  prefix: Value<string>;
  areacode: Value<string>;
  nbr: Value<string>;
};

export type ChildrenBirthRecordBase = Base &
  Person & {
    birthcertno: Value<string>;
    lifestatus: CodeDesc;

    tob: Value<string>;
  };

export type SponsoredChildrenRecordBase = Base &
  Person & {
    nric: Value<string>;
    birthcountry: CodeDescBase;
    nationality: CodeDescBase;
    lifestatus: CodeDesc;
    residentialstatus: CodeDescBase;
    scprgrantdate: Value<string>;
  };

export type HouseHoldIncomeBase = Base & {
  low: Value<string>;
  high: Value<string>;
};

export type Vehicle = Base & {
  vehicleno: Value<string>;
  type: Value<string>;
  iulabelno: Value<string>;
  make: Value<string>;
  model: Value<string>;
  chassisno: Value<string>;
  engineno: Value<string>;
  motorno: Value<string>;
  yearofmanufacture: Value<string>;
  firstregistrationdate: Value<string>;
  originalregistrationdate: Value<string>;
  coecategory: Value<string>;
  coeexpirydate: Value<string>;
  roadtaxexpirydate: Value<string>;
  quotapremium: Value<number>;
  openmarketvalue: Value<number>;
  co2emission: Value<number>;
  status: CodeDesc;
  primarycolour: Value<string>;
  secondarycolour: Value<string>;
  attachment2: Value<string>;
  attachment3: Value<string>;
  thcemission: Value<number>;
  coemission: Value<number>;
  noxemission: Value<number>;
  pmemission: Value<number>;
  enginecapacity: Value<number>;
  powerrate: Value<number>;
  effectiveownership: Value<string>;
  propellant: Value<string>;
  maximumunladenweight: Value<number>;
  maximumladenweight: Value<number>;
  minimumparfbenefit: Value<number>;
  nooftransfers: Value<number>;
  vpc: Value<string>;
};
type RangeDate = {
  startdate: Value<string>;
  enddate: Value<string>;
};

type DrivingLicenceValidity = {
  validity: CodeDesc;
  expirydate: Value<string>;
  classes: [
    {
      class: Value<string>;
      issuedate?: Value<string>;
    }
  ];
};

export type DrivingLicenceBase = Base & {
  comstatus: CodeDesc;
  totaldemeritpoints: Value<number>;
  suspension: RangeDate;
  disqualification: RangeDate;
  revocation: RangeDate;
  pdl: DrivingLicenceValidity;
  qdl: DrivingLicenceValidity;
  photocardserialno: Value<string>;
};

export type AcademicQualificationBase = Base & {
  transcripts: [
    {
      name: Value<string>;
      yearattained: Value<string>;
      results: [
        {
          subject: Value<string>;
          level: Value<string>;
          grade: Value<string>;
          subsubject: Value<string>;
          subgrade: Value<string>;
        }
      ];
      explanatorynotes: Value<string>;
    }
  ];
  certificates: [
    {
      name: Value<string>;
      content: Value<string>;
      opencertificate: {
        id: Value<number>;
        primary: Value<boolean>;
      };
      opencertificateindicator: Value<boolean>;
    }
  ];
};

export type MerdekaGenerationBase = Base & {
  eligibility: Value<boolean>;
  quantum: Value<number>;
  message: CodeDesc;
};

export type SilverSupportBase = Base & {
  eligibility: Value<boolean>;
  amount: Value<number>;
  year: Value<string>;
};

export type GSTVoucherBase = Base & {
  exclusion: Value<boolean>;
  signup: Value<boolean>;
  gstmedisave: Value<number>;
  gstregular: Value<number>;
  gstspecial: Value<number>;
  year: Value<string>;
};

export type NoticeOfAssessmentBasic = {
  amount: Value<number>;
  yearofassessment: Value<string>;
};

export type NoticeOfAssessment = {
  employment: Value<number>;
  trade: Value<number>;
  rent: Value<number>;
  interest: Value<number>;
  taxclearance: Value<string>;
  category: Value<string>;
};

export type NoticeOfAssessmentHistoryBasicBase = Base & {
  noas: NoticeOfAssessmentBasic[];
};

export type NoticeOfAssessmentHistoryFullBase = Base & {
  noas?: NoticeOfAssessmentHistory[];
  unavailable?: boolean;
};

export type CPFEmployerHistory = {
  month: Value<string>;
  employer: Value<string>;
};

export type CPFEmployerHistoryBase = Base & {
  history: CPFEmployerHistory[];
};

export type CPFContributionHistory = CPFEmployerHistory & {
  date: Value<string>;
  amount: Value<number>;
};

export type CPFContributionHistoryBase = Base & {
  history?: CPFContributionHistory[];
};

export type CPFBalance = {
  ma: Value<number>;
  oa: Value<number>;
  sa: Value<number>;
  ra: Value<number>;
};

export type CPFHousingWithdrawalDetail = {
  address: Address;
  accruedinterestamt: Value<number>;
  monthlyinstalmentamt: Value<number>;
  principalwithdrawalamt: Value<number>;
  totalamountofcpfallowedforproperty: Value<number>;
};

export type CPFHousingWithdrawalBase = Base & {
  withdrawaldetails: CPFHousingWithdrawalDetail[];
};
